<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'created', 'deleted']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    invoiceNumber: {
      type: String,
      required: false,
    },
    formContext: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newTransaction: $fn.value.pgettext('Invoice', 'New Invoice'),
    invoiceKind: {
      incoming: $fn.value.pgettext('InvoiceKind', 'Incoming Invoice'),
      outgoing: $fn.value.pgettext('InvoiceKind', 'Outgoing Invoice'),
    },
    createCorrectorTooltip: $fn.value.pgettext(
      'Invoice',
      'Create corrector invoice'
    ),
    createStornoTooltip: $fn.value.pgettext('Invoice', 'Create storno invoice'),
    createPassThroughTooltip: $fn.value.pgettext(
      'Invoice|Tooltip',
      'Rebilling the line items to a designated client.'
    ),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.invoiceKind[props.invoice.invoice_kind];
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return (
        props.invoiceNumber ||
        (props.invoice.isNew
          ? messages.value.newTransaction
          : props.invoice.invoice_number)
      );
    }
    return null;
  });
  const createCorrectorTooltip = computed(() => {
    if (loaded.value) {
      return (
        _.get(props.invoice, 'meta.modifiable.message') ||
        messages.value.createCorrectorTooltip
      );
    }
    return null;
  });
  const createStornoTooltip = computed(() => {
    if (loaded.value) {
      return (
        _.get(props.invoice, 'meta.reversible.message') ||
        messages.value.createStornoTooltip
      );
    }
    return null;
  });
  const invoiceIsReversible = computed(() => {
    return _.get(props.invoice, 'meta.reversible.result');
  });
  const invoiceIsModifiable = computed(() => {
    return _.get(props.invoice, 'meta.modifiable.result');
  });
  const invoiceIsRegistrable = computed(() => {
    return _.get(props.invoice, 'meta.registrable.result');
  });
  const invoicePassThroughIsEnabled = computed(() => {
    return (
      _.get(props.invoice, 'meta.pass_through_enabled') === true ||
      _.get(props.invoice, 'meta.pass_through_enabled.result')
    );
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.invoiceNumber,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceNumber');
    }
  );
  watch(
    () => props.formContext,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formContext');
    }
  );

  const act_3573211234170528_emit_lu7nhewvu_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3350181908999980_emit_b05u1xs51_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5072463219534047_emit_3z8490h97_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5450657804282831_emit_fanofozm9_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_995920279152457_emit_vvy0t8210_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_995920279152457_close_6u52x6o3o_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_995920279152457_showModal_y0wbnwa3c_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceEditor',
      props: {
        invoice: _.cloneDeep(props.invoice),
      },
    };
  };
  const act_8434031504612555_emit_w02srvnf4_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_142876513986814_emit_kw7d6vmiy_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4619326685596221_showModal_5ubvu07tu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLogMessageViewer',
      props: {
        invoiceId: props.invoice.id,
      },
    };
  };
  const act_5071673642893579_emit_grkxhaiux_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5071673642893579_close_4wv0mji6p_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_5071673642893579_showModal_deh85pygu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_7200560637235153_emit_j0gigsh7n_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_7200560637235153_request_7xcnkv48m_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: { invoice_id: props.invoice.id, invoice_type: 'copy' },
    };
  };
  const act_2820580198023098_emit_d6c3jb4b7_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2820580198023098_close_qhoyg183u_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_2820580198023098_showModal_1pvf376g0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreatePassThroughInvoice',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_7708892537216822_emit_djr6fdsy2_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_7708892537216822_request_q05oph71g_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: { invoice_id: props.invoice.id, invoice_type: 'storno' },
    };
  };
  const act_3708615633814391_emit_4w6knzmcv_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_3708615633814391_request_v311lsp9e_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: {
        invoice_id: props.invoice.id,
        invoice_type: 'correction',
        invoice_lines: _.map(
          props.formContext.selectedInvoiceLines,
          (p) => p.id
        ).join(','),
      },
    };
  };
  const act_540979853027208_emit_4ns5cqe0n_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_8845268238478512_emit_h6eqwak1q_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };
  const act_8646800130370252_showModal_q8sv6kz2u_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'EntityEvents',
      props: {
        entityId: props.invoice.id,
      },
    };
  };

  const act_3573211234170528_emit_lu7nhewvu = new actionTypes.EmitAction({
    actionArgs: act_3573211234170528_emit_lu7nhewvu_getActionArgs,
  });
  const inst_evh_3573211234170528_success_qyvmbo7b6 =
    new actionTypes.EventHandlerImpl({
      action: act_3573211234170528_emit_lu7nhewvu,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_3573211234170528_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3573211234170528_success_qyvmbo7b6],
    event: 'success',
  });
  const act_3350181908999980_emit_b05u1xs51 = new actionTypes.EmitAction({
    actionArgs: act_3350181908999980_emit_b05u1xs51_getActionArgs,
  });
  const inst_evh_3350181908999980_success_1dlgxjmzv =
    new actionTypes.EventHandlerImpl({
      action: act_3350181908999980_emit_b05u1xs51,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_3350181908999980_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3350181908999980_success_1dlgxjmzv],
    event: 'success',
  });
  const act_5072463219534047_emit_3z8490h97 = new actionTypes.EmitAction({
    actionArgs: act_5072463219534047_emit_3z8490h97_getActionArgs,
  });
  const inst_evh_5072463219534047_success_5b4ruzjeh =
    new actionTypes.EventHandlerImpl({
      action: act_5072463219534047_emit_3z8490h97,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_5072463219534047_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5072463219534047_success_5b4ruzjeh],
    event: 'success',
  });
  const act_5450657804282831_emit_fanofozm9 = new actionTypes.EmitAction({
    actionArgs: act_5450657804282831_emit_fanofozm9_getActionArgs,
  });
  const inst_evh_5450657804282831_success_xhfxlj4o8 =
    new actionTypes.EventHandlerImpl({
      action: act_5450657804282831_emit_fanofozm9,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_5450657804282831_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5450657804282831_success_xhfxlj4o8],
    event: 'success',
  });
  const act_995920279152457_emit_vvy0t8210 = new actionTypes.EmitAction({
    actionArgs: act_995920279152457_emit_vvy0t8210_getActionArgs,
  });
  const inst_evh_995920279152457_close_6u52x6o3o =
    new actionTypes.EventHandlerImpl({
      action: act_995920279152457_emit_vvy0t8210,
      event: 'close',
      when: inst_evh_995920279152457_close_6u52x6o3o_getWhen,
      displayName: 'emit',
    });
  const act_995920279152457_showModal_y0wbnwa3c =
    new actionTypes.ShowModalAction({
      actionArgs: act_995920279152457_showModal_y0wbnwa3c_getActionArgs,
      events: [inst_evh_995920279152457_close_6u52x6o3o],
    });
  const inst_evh_995920279152457_clickPreventStop_ekqyf3nfl =
    new actionTypes.EventHandlerImpl({
      action: act_995920279152457_showModal_y0wbnwa3c,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_995920279152457_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_995920279152457_clickPreventStop_ekqyf3nfl],
      event: 'click.prevent.stop',
    });
  const act_8434031504612555_emit_w02srvnf4 = new actionTypes.EmitAction({
    actionArgs: act_8434031504612555_emit_w02srvnf4_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_8434031504612555_clickPreventStop_qu9kawc83 =
    new actionTypes.EventHandlerImpl({
      action: act_8434031504612555_emit_w02srvnf4,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8434031504612555_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8434031504612555_clickPreventStop_qu9kawc83],
      event: 'click.prevent.stop',
    });
  const act_142876513986814_emit_kw7d6vmiy = new actionTypes.EmitAction({
    actionArgs: act_142876513986814_emit_kw7d6vmiy_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_142876513986814_reload_63s6crxpu =
    new actionTypes.EventHandlerImpl({
      action: act_142876513986814_emit_kw7d6vmiy,
      event: 'reload',
      displayName: 'reload',
    });
  const inst_evh_142876513986814_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_142876513986814_reload_63s6crxpu],
    event: 'reload',
  });
  const act_4619326685596221_showModal_5ubvu07tu =
    new actionTypes.ShowModalAction({
      actionArgs: act_4619326685596221_showModal_5ubvu07tu_getActionArgs,
    });
  const inst_evh_4619326685596221_clickPreventStop_axjr90xmn =
    new actionTypes.EventHandlerImpl({
      action: act_4619326685596221_showModal_5ubvu07tu,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_4619326685596221_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4619326685596221_clickPreventStop_axjr90xmn],
      event: 'click.prevent.stop',
    });
  const act_5071673642893579_emit_grkxhaiux = new actionTypes.EmitAction({
    actionArgs: act_5071673642893579_emit_grkxhaiux_getActionArgs,
  });
  const inst_evh_5071673642893579_close_4wv0mji6p =
    new actionTypes.EventHandlerImpl({
      action: act_5071673642893579_emit_grkxhaiux,
      event: 'close',
      when: inst_evh_5071673642893579_close_4wv0mji6p_getWhen,
      displayName: 'emit',
    });
  const act_5071673642893579_showModal_deh85pygu =
    new actionTypes.ShowModalAction({
      actionArgs: act_5071673642893579_showModal_deh85pygu_getActionArgs,
      displayName: 'registerInvoice',
      events: [inst_evh_5071673642893579_close_4wv0mji6p],
    });
  const inst_evh_5071673642893579_click_zubfm4uh5 =
    new actionTypes.EventHandlerImpl({
      action: act_5071673642893579_showModal_deh85pygu,
      event: 'click',
      displayName: 'registerInvoice',
    });
  const inst_evh_5071673642893579_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5071673642893579_click_zubfm4uh5],
    event: 'click',
  });
  const act_7200560637235153_emit_j0gigsh7n = new actionTypes.EmitAction({
    actionArgs: act_7200560637235153_emit_j0gigsh7n_getActionArgs,
  });
  const inst_evh_7200560637235153_success_do5rnw2g5 =
    new actionTypes.EventHandlerImpl({
      action: act_7200560637235153_emit_j0gigsh7n,
      event: 'success',
      displayName: 'emit',
    });
  const act_7200560637235153_request_7xcnkv48m = new actionTypes.RequestAction({
    actionArgs: act_7200560637235153_request_7xcnkv48m_getActionArgs,
    displayName: 'createInvoicecopy',
    events: [inst_evh_7200560637235153_success_do5rnw2g5],
  });
  const inst_evh_7200560637235153_click_2o21nxcnd =
    new actionTypes.EventHandlerImpl({
      action: act_7200560637235153_request_7xcnkv48m,
      event: 'click',
      displayName: 'createInvoicecopy',
    });
  const inst_evh_7200560637235153_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7200560637235153_click_2o21nxcnd],
    event: 'click',
  });
  const act_2820580198023098_emit_d6c3jb4b7 = new actionTypes.EmitAction({
    actionArgs: act_2820580198023098_emit_d6c3jb4b7_getActionArgs,
  });
  const inst_evh_2820580198023098_close_qhoyg183u =
    new actionTypes.EventHandlerImpl({
      action: act_2820580198023098_emit_d6c3jb4b7,
      event: 'close',
      when: inst_evh_2820580198023098_close_qhoyg183u_getWhen,
      displayName: 'emit',
    });
  const act_2820580198023098_showModal_1pvf376g0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2820580198023098_showModal_1pvf376g0_getActionArgs,
      displayName: 'createPassThroughInvoice',
      events: [inst_evh_2820580198023098_close_qhoyg183u],
    });
  const inst_evh_2820580198023098_click_plvjsmxtd =
    new actionTypes.EventHandlerImpl({
      action: act_2820580198023098_showModal_1pvf376g0,
      event: 'click',
      displayName: 'createPassThroughInvoice',
    });
  const inst_evh_2820580198023098_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2820580198023098_click_plvjsmxtd],
    event: 'click',
  });
  const act_7708892537216822_emit_djr6fdsy2 = new actionTypes.EmitAction({
    actionArgs: act_7708892537216822_emit_djr6fdsy2_getActionArgs,
  });
  const inst_evh_7708892537216822_success_lcqqmu07c =
    new actionTypes.EventHandlerImpl({
      action: act_7708892537216822_emit_djr6fdsy2,
      event: 'success',
      displayName: 'emit',
    });
  const act_7708892537216822_request_q05oph71g = new actionTypes.RequestAction({
    actionArgs: act_7708892537216822_request_q05oph71g_getActionArgs,
    displayName: 'createStornoInvoice',
    events: [inst_evh_7708892537216822_success_lcqqmu07c],
  });
  const inst_evh_7708892537216822_click_lvk9aqmbm =
    new actionTypes.EventHandlerImpl({
      action: act_7708892537216822_request_q05oph71g,
      event: 'click',
      displayName: 'createStornoInvoice',
    });
  const inst_evh_7708892537216822_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7708892537216822_click_lvk9aqmbm],
    event: 'click',
  });
  const act_3708615633814391_emit_4w6knzmcv = new actionTypes.EmitAction({
    actionArgs: act_3708615633814391_emit_4w6knzmcv_getActionArgs,
  });
  const inst_evh_3708615633814391_success_bngbkv52x =
    new actionTypes.EventHandlerImpl({
      action: act_3708615633814391_emit_4w6knzmcv,
      event: 'success',
      displayName: 'emit',
    });
  const act_3708615633814391_request_v311lsp9e = new actionTypes.RequestAction({
    actionArgs: act_3708615633814391_request_v311lsp9e_getActionArgs,
    displayName: 'createCorrectorIvoice',
    events: [inst_evh_3708615633814391_success_bngbkv52x],
  });
  const inst_evh_3708615633814391_click_ssfwn50si =
    new actionTypes.EventHandlerImpl({
      action: act_3708615633814391_request_v311lsp9e,
      event: 'click',
      displayName: 'createCorrectorIvoice',
    });
  const inst_evh_3708615633814391_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3708615633814391_click_ssfwn50si],
    event: 'click',
  });
  const act_540979853027208_emit_4ns5cqe0n = new actionTypes.EmitAction({
    actionArgs: act_540979853027208_emit_4ns5cqe0n_getActionArgs,
  });
  const inst_evh_540979853027208_success_i6vw955b8 =
    new actionTypes.EventHandlerImpl({
      action: act_540979853027208_emit_4ns5cqe0n,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_540979853027208_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_540979853027208_success_i6vw955b8],
    event: 'success',
  });
  const act_8845268238478512_emit_h6eqwak1q = new actionTypes.EmitAction({
    actionArgs: act_8845268238478512_emit_h6eqwak1q_getActionArgs,
  });
  const inst_evh_8845268238478512_success_ltdwgu1o4 =
    new actionTypes.EventHandlerImpl({
      action: act_8845268238478512_emit_h6eqwak1q,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_8845268238478512_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8845268238478512_success_ltdwgu1o4],
    event: 'success',
  });
  const act_8646800130370252_showModal_q8sv6kz2u =
    new actionTypes.ShowModalAction({
      actionArgs: act_8646800130370252_showModal_q8sv6kz2u_getActionArgs,
    });
  const inst_evh_8646800130370252_clickPreventStop_g4d7urgx2 =
    new actionTypes.EventHandlerImpl({
      action: act_8646800130370252_showModal_q8sv6kz2u,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8646800130370252_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8646800130370252_clickPreventStop_g4d7urgx2],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_3573211234170528_success_qyvmbo7b6,
    inst_evh_3573211234170528_success,
    inst_evh_3350181908999980_success_1dlgxjmzv,
    inst_evh_3350181908999980_success,
    inst_evh_5072463219534047_success_5b4ruzjeh,
    inst_evh_5072463219534047_success,
    inst_evh_5450657804282831_success_xhfxlj4o8,
    inst_evh_5450657804282831_success,
    inst_evh_995920279152457_close_6u52x6o3o,
    inst_evh_995920279152457_clickPreventStop_ekqyf3nfl,
    inst_evh_995920279152457_clickPreventStop,
    inst_evh_8434031504612555_clickPreventStop_qu9kawc83,
    reload: inst_evh_8434031504612555_clickPreventStop_qu9kawc83,
    inst_evh_8434031504612555_clickPreventStop,
    inst_evh_142876513986814_reload_63s6crxpu,
    inst_evh_142876513986814_reload,
    inst_evh_4619326685596221_clickPreventStop_axjr90xmn,
    inst_evh_4619326685596221_clickPreventStop,
    inst_evh_5071673642893579_close_4wv0mji6p,
    inst_evh_5071673642893579_click_zubfm4uh5,
    registerInvoice: inst_evh_5071673642893579_click_zubfm4uh5,
    inst_evh_5071673642893579_click,
    inst_evh_7200560637235153_success_do5rnw2g5,
    inst_evh_7200560637235153_click_2o21nxcnd,
    createInvoicecopy: inst_evh_7200560637235153_click_2o21nxcnd,
    inst_evh_7200560637235153_click,
    inst_evh_2820580198023098_close_qhoyg183u,
    inst_evh_2820580198023098_click_plvjsmxtd,
    createPassThroughInvoice: inst_evh_2820580198023098_click_plvjsmxtd,
    inst_evh_2820580198023098_click,
    inst_evh_7708892537216822_success_lcqqmu07c,
    inst_evh_7708892537216822_click_lvk9aqmbm,
    createStornoInvoice: inst_evh_7708892537216822_click_lvk9aqmbm,
    inst_evh_7708892537216822_click,
    inst_evh_3708615633814391_success_bngbkv52x,
    inst_evh_3708615633814391_click_ssfwn50si,
    createCorrectorIvoice: inst_evh_3708615633814391_click_ssfwn50si,
    inst_evh_3708615633814391_click,
    inst_evh_540979853027208_success_i6vw955b8,
    inst_evh_540979853027208_success,
    inst_evh_8845268238478512_success_ltdwgu1o4,
    inst_evh_8845268238478512_success,
    inst_evh_8646800130370252_clickPreventStop_g4d7urgx2,
    inst_evh_8646800130370252_clickPreventStop,
  };

  const evh_3573211234170528_success_qyvmbo7b6 = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_success_qyvmbo7b6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_success = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3350181908999980_success_1dlgxjmzv = async (event, scope) => {
    return await actions.inst_evh_3350181908999980_success_1dlgxjmzv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3350181908999980_success = async (event, scope) => {
    return await actions.inst_evh_3350181908999980_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5072463219534047_success_5b4ruzjeh = async (event, scope) => {
    return await actions.inst_evh_5072463219534047_success_5b4ruzjeh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5072463219534047_success = async (event, scope) => {
    return await actions.inst_evh_5072463219534047_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5450657804282831_success_xhfxlj4o8 = async (event, scope) => {
    return await actions.inst_evh_5450657804282831_success_xhfxlj4o8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5450657804282831_success = async (event, scope) => {
    return await actions.inst_evh_5450657804282831_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_close_6u52x6o3o = async (event, scope) => {
    return await actions.inst_evh_995920279152457_close_6u52x6o3o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_clickPreventStop_ekqyf3nfl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_995920279152457_clickPreventStop_ekqyf3nfl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_995920279152457_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8434031504612555_clickPreventStop_qu9kawc83 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8434031504612555_clickPreventStop_qu9kawc83.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8434031504612555_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8434031504612555_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_142876513986814_reload_63s6crxpu = async (event, scope) => {
    return await actions.inst_evh_142876513986814_reload_63s6crxpu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_142876513986814_reload = async (event, scope) => {
    return await actions.inst_evh_142876513986814_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4619326685596221_clickPreventStop_axjr90xmn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4619326685596221_clickPreventStop_axjr90xmn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4619326685596221_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4619326685596221_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_close_4wv0mji6p = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_close_4wv0mji6p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_click_zubfm4uh5 = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_click_zubfm4uh5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_click = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_success_do5rnw2g5 = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_success_do5rnw2g5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_click_2o21nxcnd = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_click_2o21nxcnd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_click = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_close_qhoyg183u = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_close_qhoyg183u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_click_plvjsmxtd = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_click_plvjsmxtd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_click = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_success_lcqqmu07c = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_success_lcqqmu07c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_click_lvk9aqmbm = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_click_lvk9aqmbm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_click = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_success_bngbkv52x = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_success_bngbkv52x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_click_ssfwn50si = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_click_ssfwn50si.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_click = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_540979853027208_success_i6vw955b8 = async (event, scope) => {
    return await actions.inst_evh_540979853027208_success_i6vw955b8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_540979853027208_success = async (event, scope) => {
    return await actions.inst_evh_540979853027208_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8845268238478512_success_ltdwgu1o4 = async (event, scope) => {
    return await actions.inst_evh_8845268238478512_success_ltdwgu1o4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8845268238478512_success = async (event, scope) => {
    return await actions.inst_evh_8845268238478512_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8646800130370252_clickPreventStop_g4d7urgx2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8646800130370252_clickPreventStop_g4d7urgx2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8646800130370252_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8646800130370252_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted class="mb-0">
        {{ resourceTitle }}
      </ItpText>
      <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <UcInvoiceTypeBadge
      :value="invoice.invoice_type"
      :kind="invoice.invoice_kind"
    >
    </UcInvoiceTypeBadge>
    <UcInvoiceStatusBadge :value="invoice.status"> </UcInvoiceStatusBadge>
    <div v-if="invoice.tags && invoice.tags.length">
      <span
        class="badge bg-secondary mx-1 shadow-none"
        v-for="tag in invoice.tags"
      >
        {{ tag }}
      </span>
    </div>
    <div :title="gettext('Closed')" v-if="invoice.isReadOnly">
      <ItpIcon :icon="$config.lockIcon"> </ItpIcon>
    </div>
    <div class="text-green-500" v-if="invoice.authority_transaction_id">
      <div :title="invoice.authority_transaction_id">
        <ItpIcon icon="fluent:cloud-sync-complete-16-regular"> </ItpIcon>
      </div>
      <div :title="JSON.stringify(invoice.authority_transaction_status)">
        <ItpIcon icon="fa-question-circle"> </ItpIcon>
      </div>
    </div>
    <ItpBox direction="column" v-if="invoice.reference_invoice">
      <ItpText muted class="mb-0">
        {{ pgettext('Invoice', 'Reference invoice') }}
      </ItpText>
      <UcInvoiceMiniCard :item="invoice.reference_invoice" no-icon>
      </UcInvoiceMiniCard>
    </ItpBox>
    <ItpBox
      direction="column"
      v-if="invoice.referencing_invoices && invoice.referencing_invoices.length"
    >
      <ItpText muted class="mb-0">
        {{ pgettext('Invoice', 'Referencing invoices') }}
      </ItpText>
      <ItpBox>
        <UcInvoiceMiniCard
          :key="obj.id"
          :item="obj"
          no-icon
          class="me-2"
          v-for="obj in invoice.referencing_invoices"
        >
        </UcInvoiceMiniCard>
      </ItpBox>
    </ItpBox>
    <Icon
      :icon="'flagpack:' + invoice.language"
      :title="pgettext('Invoice', 'Invoice Language')"
      v-if="!invoice.isNew"
    >
    </Icon>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <BtnIssueInvoice
        :invoice="invoice"
        v-if="
          invoice.meta && invoice.meta.issuance_status.status == 'not_started'
        "
        @success="evh_3573211234170528_success($event, {})"
      >
      </BtnIssueInvoice>
      <BtnSyncInvoice
        :invoice="invoice"
        v-if="
          invoice.meta &&
          invoice.meta.billing_service_status.status == 'not_started'
        "
        @success="evh_3350181908999980_success($event, {})"
      >
      </BtnSyncInvoice>
      <div class="ms-auto"></div>
      <BtnReportInvoiceData
        :resource="invoice"
        v-if="
          invoice.meta &&
          (invoice.meta.data_reporting_status.status == 'not_started' ||
            invoice.meta.data_reporting_status.status == 'error')
        "
        @success="evh_5072463219534047_success($event, {})"
      >
      </BtnReportInvoiceData>
      <BtnSyncResource
        :resource="invoice"
        target="irent"
        confirmMessage="A számlát csak egyszer lehet felölteni a régi rendszerbe,
feltöltés után a számlát már nem lehet módosítani."
        v-if="
          invoice.meta &&
          invoice.meta.sync_to_external_system_status.status == 'not_started'
        "
        @success="evh_5450657804282831_success($event, {})"
      >
      </BtnSyncResource>
      <ItpButton
        :icon="$config.editIcon"
        :tooltip="gettext('Edit')"
        variant="light"
        v-if="!invoice.isNew && !invoice.isReadOnly"
        @click.prevent.stop="evh_995920279152457_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!invoice.isNew"
        @click.prevent.stop="evh_8434031504612555_clickPreventStop($event, {})"
      >
      </ItpButton>
      <UcInvoiceViewDocumentDropdown
        :invoice="invoice"
        @reload="evh_142876513986814_reload($event, {})"
      >
      </UcInvoiceViewDocumentDropdown>
      <ItpButton
        variant="light"
        icon="icon-park-outline:preview-open"
        :href="invoice.preview_url"
        :tooltip="pgettext('Invoice', 'Open preview')"
        target="_blank"
        use-auth
        show-spinner
        v-if="invoice.preview_url"
      >
      </ItpButton>
      <ItpButton
        variant="light"
        icon="icon-park-outline:log"
        :tooltip="pgettext('Invoice', 'Show log messages')"
        v-if="invoice.log_messages"
        @click.prevent.stop="evh_4619326685596221_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown
        right
        variant="light"
        no-caret
        class="border"
        v-if="!invoice.isNew"
      >
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Register invoice')"
          :tooltip="
            pgettext('Invoice', 'Add this invoice to the invoice register')
          "
          variant="default"
          v-if="invoiceIsRegistrable"
          @click="evh_5071673642893579_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnCopyResource', 'Copy')"
          :tooltip="pgettext('Invoice', 'Create new invoice from this one')"
          variant="default"
          @click="evh_7200560637235153_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Create pass-through')"
          :tooltip="messages.createPassThroughTooltip"
          variant="default"
          :disabled="!invoicePassThroughIsEnabled"
          v-if="!invoice.isNew"
          @click="evh_2820580198023098_click($event, {})"
        >
        </ItpButton>
        <hr />
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Create storno')"
          :tooltip="createStornoTooltip"
          variant="default"
          :disabled="!invoiceIsReversible"
          v-if="!invoice.isNew"
          @click="evh_7708892537216822_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          variant="default"
          :text="pgettext('Invoice', 'Create corrector')"
          :tooltip="createCorrectorTooltip"
          :disabled="!invoiceIsModifiable"
          v-if="!invoice.isNew"
          @click="evh_3708615633814391_click($event, {})"
        >
        </ItpButton>
        <BtnLockResource
          no-icon
          dropdown-item
          :resource="invoice"
          :tooltip="pgettext('Invoice', 'Lock invoice')"
          v-if="!invoice.isNew && !invoice.isReadOnly"
          @success="evh_540979853027208_success($event, {})"
        >
        </BtnLockResource>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="invoice"
          :tooltip="pgettext('Invoice', 'Delete invoice')"
          v-if="!invoice.isNew && !invoice.isReadOnly"
          @success="evh_8845268238478512_success($event, {})"
        >
        </BtnDeleteResource>
        <hr />
        <ItpButton
          dropdown-item
          :text="pgettext('BtnResourceHistory', 'History')"
          :tooltip="pgettext('Invoice', 'Invoice history')"
          variant="default"
          @click.prevent.stop="
            evh_8646800130370252_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </BDropdown>
    </div>
  </ItpBox>
</template>
